import type { SettingsTranslations } from "@/shared/lang/interfaces/settings";

import type {
  OrganizationWebhookDeliveryStatus,
  OrganizationWebhookEvent,
  OrganizationWebhookEventType,
} from "../../services/organizationWebhooksService";

type SettingsKey = keyof SettingsTranslations;
type Translate = (key: SettingsKey) => string;

/**
 * Enum → i18n key. Typed against the namespace so a backend value without a label fails the build rather than
 * rendering a raw key; a value the frontend does not know yet falls back to the raw enum string (see
 * `eventTypeLabel` / `eventLabel`).
 */
export function deliveryStatusLabelKey(status: OrganizationWebhookDeliveryStatus): SettingsKey {
  switch (status) {
    case "pending":
      return "webhooks_status_pending";
    case "delivered":
      return "webhooks_status_delivered";
    case "failed":
      return "webhooks_status_failed";
    case "exhausted":
      return "webhooks_status_exhausted";
  }
}

/** Exhaustive over the union: a group added to the type without a label here fails the build. */
export const EVENT_TYPE_LABEL_KEYS: Record<OrganizationWebhookEventType, SettingsKey> = {
  payments: "webhooks_type_payments",
  identities: "webhooks_type_identities",
  cards: "webhooks_type_cards",
  wallets: "webhooks_type_wallets",
  test: "webhooks_type_test",
};

/** Exhaustive over the union: an event added to the type without a label here fails the build. */
export const EVENT_LABEL_KEYS: Record<OrganizationWebhookEvent, SettingsKey> = {
  payin_created: "webhooks_event_payin_created",
  payin_pending: "webhooks_event_payin_pending",
  payin_approved: "webhooks_event_payin_approved",
  payin_confirmed: "webhooks_event_payin_confirmed",
  payin_settled: "webhooks_event_payin_settled",
  payin_rejected: "webhooks_event_payin_rejected",
  payin_reversed: "webhooks_event_payin_reversed",
  payout_created: "webhooks_event_payout_created",
  payout_processing: "webhooks_event_payout_processing",
  payout_paid: "webhooks_event_payout_paid",
  payout_failed: "webhooks_event_payout_failed",
  payout_cancelled: "webhooks_event_payout_cancelled",
  payout_reversed: "webhooks_event_payout_reversed",
  swap_completed: "webhooks_event_swap_completed",
  identity_created: "webhooks_event_identity_created",
  kyc_approved: "webhooks_event_kyc_approved",
  kyc_rejected: "webhooks_event_kyc_rejected",
  cardholder_created: "webhooks_event_cardholder_created",
  card_created: "webhooks_event_card_created",
  card_status_changed: "webhooks_event_card_status_changed",
  card_transaction_authorized: "webhooks_event_card_transaction_authorized",
  card_transaction_declined: "webhooks_event_card_transaction_declined",
  card_transaction_refunded: "webhooks_event_card_transaction_refunded",
  card_transaction_reversed: "webhooks_event_card_transaction_reversed",
  card_topped_up: "webhooks_event_card_topped_up",
  card_deposit_received: "webhooks_event_card_deposit_received",
  card_withdrawn: "webhooks_event_card_withdrawn",
  card_wallet_recharged: "webhooks_event_card_wallet_recharged",
  wallet_created: "webhooks_event_wallet_created",
  ping: "webhooks_event_ping",
};

/**
 * Own-property lookup, so a value the backend started sending before this build knew it (or a key like
 * `toString`) yields `undefined` instead of an inherited member.
 */
function lookup<K extends string>(map: Record<K, SettingsKey>, value: string): SettingsKey | undefined {
  return Object.prototype.hasOwnProperty.call(map, value) ? map[value as K] : undefined;
}

export function eventTypeLabelKey(type: string): SettingsKey | undefined {
  return lookup(EVENT_TYPE_LABEL_KEYS, type);
}

export function eventLabelKey(event: string): SettingsKey | undefined {
  return lookup(EVENT_LABEL_KEYS, event);
}

/** Human label of a group, or the raw value when this build does not know it yet. */
export function eventTypeLabel(t: Translate, type: string): string {
  const key = eventTypeLabelKey(type);
  return key ? t(key) : type;
}

/** Human label of an event, or the raw value when this build does not know it yet. */
export function eventLabel(t: Translate, event: string): string {
  const key = eventLabelKey(event);
  return key ? t(key) : event;
}
